/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { noopFn } from '../../../Utilities/propHelpers/defaultProps.js';
import SideNav from '../SideNav/SideNav.js';

var _excluded = ["className", "open", "onToggle", "items", "inverse"],
  _excluded2 = ["flyoutItems"];
var WorldLevelNav = function WorldLevelNav(_ref) {
  var className = _ref.className,
    _ref$open = _ref.open,
    open = _ref$open === void 0 ? true : _ref$open,
    _ref$onToggle = _ref.onToggle,
    onToggle = _ref$onToggle === void 0 ? noopFn : _ref$onToggle,
    items = _ref.items,
    _ref$inverse = _ref.inverse,
    inverse = _ref$inverse === void 0 ? false : _ref$inverse,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isMobile = _useState4[0],
    setIsMobile = _useState4[1];
  var _useState5 = useState(isMobile ? 'top' : 'right'),
    _useState6 = _slicedToArray(_useState5, 2),
    collapseButtonLocation = _useState6[0],
    setCollapseButtonLocation = _useState6[1];
  var classes = classnames('cpc--world-level-nav', "cpc--world-level-nav--".concat(isMobile ? 'mobile' : 'non-mobile'), className);

  /**
   * When converting into mobile view there is no room for flyouts anymore. To still be able to display all sidenav items all flyouts are converted to dropdown items
   * @param {*} sidenavItems
   * @param {*} mobile
   * @returns
   */
  var convertItems = function convertItems(sidenavItems, mobile) {
    if (mobile) {
      return sidenavItems.map(function (item) {
        if (item.flyoutItems) {
          var flyoutItems = item.flyoutItems,
            rest = _objectWithoutProperties(item, _excluded2);
          return _objectSpread2({
            items: flyoutItems
          }, rest);
        }
        return item;
      });
    }
    return sidenavItems;
  };
  useEffect(function () {
    setIsOpen(open);
  }, [open]);
  useEffect(function () {
    if (!!window && !!document && window.innerWidth < 672) {
      onToggle();
      setIsMobile(true);
      setCollapseButtonLocation('top');
    }
    setIsOpen(!!window && !!document && window.innerWidth >= 672 && open);
  }, [open, onToggle]);
  var resizeEventListener = useCallback(function (e) {
    if (window.innerWidth < 672) {
      if (isOpen) {
        setIsOpen(false);
        onToggle(e, false);
      }
      setIsMobile(true);
      setCollapseButtonLocation('top');
    } else {
      setIsMobile(false);
      setCollapseButtonLocation('right');
    }
  }, [isOpen, onToggle]);
  useEffect(function () {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      window.addEventListener('resize', resizeEventListener);
    }
    return function () {
      return window.removeEventListener('resize', resizeEventListener);
    };
  }, [resizeEventListener]);
  return /*#__PURE__*/React.createElement(SideNav, _extends({}, props, {
    inverse: inverse,
    onToggle: onToggle,
    className: classes,
    collapseButtonLocation: collapseButtonLocation,
    showMenuItems: true,
    showHeader: !isMobile,
    open: isOpen,
    items: convertItems(items, isMobile)
  }));
};
WorldLevelNav.propTypes = {
  /**
   * The href for the page the user is currently visiting. If this is not provided it will
   * attempt to get the location off of the window's pathname. This can be useful for worlds
   * that do not use React Router and re-render on every page load or if the activeHref
   * needs to be set manually.
   */
  activeHref: PropTypes.string,
  /**
   * A custom class name to be applied to the Nav's `nav` element.
   */
  className: PropTypes.string,
  /**
   * The items for the navigation to render. If nested items are provided a sub menu will be created instead
   * with the label as the title of the menu and the href for the menu will be ignored. If you're using
   * React router you may also provide the to prop to the provided LinkComponent in place of the href. To
   * add an icon to the navigation Link, you must pass an 16px icon component as prop.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    href: PropTypes.string,
    icon: PropTypes.elementType,
    to: PropTypes.string,
    menuButtonComponent: PropTypes.elementType,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    open: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.shape({
      href: PropTypes.string,
      to: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    }))
  })).isRequired,
  /**
   * A component or element to use for the link element. If you're using something like React Router
   * this can be a helpful backdoor to pass in your own component, so the page doesn't re-render.
   * Currently only the `to` and href props are supported to be passed into the link component.
   * If the nav item includes an href prop the link component will default to using an a tag.
   */
  linkComponent: PropTypes.elementType,
  /**
   * A callback function to call when the Side Nav toggle button is clicked.
   */
  onToggle: PropTypes.func,
  /**
   * If side nav should be open or not.
   */
  open: PropTypes.bool,
  /**
   * The title to be rendered at the top of the side nav component.
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /**
   * Boolean to render nav in g90 theme.
   */
  inverse: PropTypes.bool
};
var WorldLevelNav$1 = WorldLevelNav;

export { WorldLevelNav$1 as default };
